import {
  Component,
  OnDestroy,
  OnInit,
  computed,
  effect,
  inject,
  signal
} from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { ActivatedRoute, Router } from '@angular/router';
import { Store } from '@ngrx/store';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import localize from 'ajv-i18n';
import { Ajv2020, ErrorObject, JSONSchemaType } from 'ajv/dist/2020';
import {
  Observable,
  Subscription,
  catchError,
  filter,
  finalize,
  map,
  of,
  take,
  tap
} from 'rxjs';

import { AppRoutes } from '@gfe/app.paths';
import { TransformerInterface } from '@gfe/core/models/entities/entity-visualization-interface';
import { GbeError } from '@gfe/core/models/gbe/gbe-error.model';
import { EntityLabelsTranslationService } from '@gfe/core/services/entity-labels-translation/entity-labels-translation.service';
import {
  selectAvailableTransformers,
  selectDataspaceState
} from '@gfe/core/store/dataspace/dataspace.selectors';
import {
  getPropertyTypes,
  isBoolStringNumber,
  isBooleanString,
  isCsvCompatible,
  isTruthyString,
  matchesPatternProperties,
  numberRegex
} from '@gfe/core/utils/json-schema-utils';

import { Label, LabelValueType } from '@core/models/labels.model';
import {
  DataModel,
  DataTransformerRequest,
  DataTransformerSuccessResponse,
  Transformer
} from '@core/models/transformers.model';
import { CsvTransformerService } from '@core/services/csv-transformer/csv-transformer.service';
import { occurrenceText } from '@core/services/gbe-error-parser/entity-ref-labels';
import { GbeErrorParserService } from '@core/services/gbe-error-parser/gbe-error-parser.service';
import { ICONS } from '@core/services/icons/icons.service';
import { NotificationService } from '@core/services/notification/notification.service';
import { PlansService } from '@core/services/plans/plans.service';
import { selectLabels } from '@core/store/admin/tags-labels/tags-labels.selectors';
import { selectPlan } from '@core/store/plan/plan.selectors';
import { AutoUnsubscribe } from '@core/utils/auto-unsubscribe';
import { isNil, stripNullFields } from '@core/utils/shared-utils';

import {
  ButtonSelectComponent,
  ButtonSelectValue
} from '@shared/components/button-select/button-select.component';
import { ButtonComponent } from '@shared/components/button/button.component';
import { SelectId, SelectItem } from '@shared/components/select/select-item.model';
import { SpinnerComponent } from '@shared/components/spinner/spinner.component';
import { ErrorsListComponent } from '@shared/components/table/errors-list/errors-list.component';
import { TableComponent } from '@shared/components/table/table.component';
import {
  RowEvent,
  TableAction,
  TableColumn,
  TableConfig,
  TableRowError
} from '@shared/components/table/table.model';
import {
  FilesDropped,
  UploadClicked,
  UploadZoneComponent,
  UploadZoneConfig
} from '@shared/components/upload-zone/upload-zone.component';
import { CsvParserService } from '@shared/services/csv-parser.service';
import { FileTransferService } from '@shared/services/file-transfer/file-transfer.service';
import { AvailableLanguages } from '@shared/types/translation.type';

import {
  NumberedRow,
  ReferenceCell,
  findReferenceCells,
  referenceColumnsOf
} from './reference-cells';

interface TableRow {
  [key: string]: string | number | boolean | null | undefined;
}

/**
 * Prefix used for custom-label columns in the CSV (e.g. `cl_my_label`). These columns are
 * not part of the entity JSON schema: the backend transformer maps any `cl_`-prefixed column
 * into `custom_data.labels`. The available label definitions (and their value types) come from
 * the labels registry in the store, not from the schema.
 */
const CUSTOM_LABEL_PREFIX = 'cl_';

/** Sort key for the errors list. An error naming no row sorts last rather than first. */
function rowOrder(error: TableRowError<TableRow>): number {
  return error.rowNumber ?? Number.MAX_SAFE_INTEGER;
}

@Component({
  selector: 'app-transformer-upload',
  imports: [
    UploadZoneComponent,
    TableComponent,
    ErrorsListComponent,
    TranslatePipe,
    ButtonComponent,
    ButtonSelectComponent,
    SpinnerComponent
  ],
  templateUrl: './transformer-upload.component.html'
})
export class TransformerUploadComponent
  extends AutoUnsubscribe
  implements OnInit, OnDestroy
{
  private readonly ajv = new Ajv2020({ allErrors: true, strict: false });
  private readonly store = inject(Store);
  private readonly gbeErrorParserService = inject(GbeErrorParserService);
  private readonly router = inject(Router);
  private readonly activatedRoute = inject(ActivatedRoute);
  private readonly csvTransformersService = inject(CsvTransformerService);
  private readonly plansService = inject(PlansService);
  private readonly entityLabelsTranslationService = inject(EntityLabelsTranslationService);
  private readonly fileTransferService = inject(FileTransferService);
  private readonly notificationService = inject(NotificationService);
  private readonly translate = inject(TranslateService);

  showPicker = signal<boolean>(false);

  /** Whether a CSV file is being dragged over the table, to show the drop overlay. */
  protected readonly isDraggingCsv = signal<boolean>(false);

  protected readonly isParsingCsv = signal<boolean>(false);

  /**
   * Cancelled when a newer file or a reset supersedes it: the page stays usable while a file
   * parses, and a stale parse would otherwise append its rows and clear the indicator of the
   * parse that replaced it.
   */
  private parseSubscription?: Subscription;

  private routeParams = toSignal(this.activatedRoute.paramMap);
  private queryParams = toSignal(this.activatedRoute.queryParamMap);
  private entityType = computed(() => {
    return this.routeParams()?.get(AppRoutes.data.view.params.entity) || '';
  });
  private allTransformers = this.store.selectSignal(selectAvailableTransformers);
  private availableLabels = this.store.selectSignal(selectLabels);
  private readonly plan = this.store.selectSignal(selectPlan);

  /**
   * The plan this import writes into. Read from the route rather than the store: the store's
   * plan is populated asynchronously, so an import submitted before it arrives would read as
   * "no plan" and write the rows into global data instead.
   */
  private readonly routePlanId = computed<string | null>(
    () => this.routeParams()?.get(AppRoutes.plan.plans.view.params.planId) ?? null
  );

  /**
   * Name of the plan this import writes into, or null in global data. Shown in the header:
   * this screen is the one place a misread of the target could destroy a plan's data or
   * pollute global data with rows meant for one plan. The name trails the route by however
   * long the plan takes to load; the target itself does not.
   */
  protected readonly targetPlanName = computed<string | null>(
    () => this.plan()?.name ?? null
  );

  /**
   * Maps each custom-label column name (`cl_<labelKey>`) to its label definition, derived from
   * the labels registry. Used to (1) recognize `cl_*` columns as valid (not "ignored") and
   * (2) coerce their CSV values to the label's declared value type.
   */
  protected customLabelColumns = computed<Map<string, Label>>(() => {
    const map = new Map<string, Label>();
    for (const label of this.availableLabels()) {
      map.set(`${CUSTOM_LABEL_PREFIX}${label.label}`, label);
    }
    return map;
  });
  private availableTransformers = computed<TransformerInterface[]>(() => {
    const allTransformers = this.allTransformers();
    const entityType = this.entityType().toUpperCase();
    return allTransformers.filter((t: TransformerInterface) => {
      return (
        t.transformerInfo.transformer.generatedEntities.includes(entityType) &&
        t.transformerInfo.dataModels.length > 0 &&
        isCsvCompatible(t.transformerInfo.dataModels[0].metadata.schema)
      );
    });
  });
  private transformerName = computed<string | undefined>(() => {
    return this.queryParams()?.get('transformer') ?? undefined;
  });

  protected makeTableId(): string {
    return `transformer-upload-${this.transformerName()}`;
  }

  protected selectedTransformer = computed<TransformerInterface | undefined>(() => {
    const transformerName = this.transformerName();
    if (isNil(transformerName)) {
      return undefined;
    }
    const allTransformers = this.availableTransformers();
    const selectedTransformer = allTransformers.find(
      (t) => t.transformerDisplayLabel() == transformerName
    );
    if (isNil(selectedTransformer)) {
      console.error(
        'selected transformer cannot be found in the list of all transformers: ',
        transformerName
      );
    }
    return selectedTransformer;
  });

  // table configuration
  tableConfig: TableConfig<TableRow> = {
    bordered: true,
    editable: {
      inline: true,
      newTab: false
    },
    horizontalScroll: true,
    errors: true,
    columnTooltips: true,
    columnColors: true,
    rowNumbers: true,
    columnActionSettings: {
      movable: true,
      resizeable: true,
      sortable: false,
      filterable: false,
      groupable: false,
      hideable: false
    }
  };
  rows = signal<TableRow[]>([]);
  fileColumns = signal<TableColumn<TableRow>[]>([]);

  columns = computed<TableColumn<TableRow>[]>(() => {
    const fileCols = this.fileColumns();
    const baseCols = this.transformerBaseColumns();
    const ignoredKeys = this.ignoredColumnKeys();

    // Add columns from baseCols that aren't in fileCols
    const newCols: TableColumn<TableRow>[] = [];
    for (const col of baseCols) {
      if (!fileCols.some((c) => c.key === col.key)) {
        newCols.push(col);
      }
    }

    // Merge all columns
    const allCols = [...fileCols, ...newCols];

    // Trigger reactivity on language change for translations
    this.currentLang();

    return allCols.map((col) => {
      const colKey = String(col.key);
      const isIgnored = ignoredKeys.has(colKey);

      // Build base column configuration
      const processedCol: TableColumn<TableRow> = {
        config: {
          sortable: false,
          filterable: false,
          groupable: false,
          hideable: false,
          orderable: false,
          resizeable: true
        },
        ...col
      };

      // Apply warning styling for ignored columns
      if (isIgnored) {
        processedCol.color = '#f59e0b';

        const suffix = this.translate.instant('features.data.upload.column_ignored_suffix');

        processedCol.tooltip = this.translate.instant(
          'features.data.upload.column_ignored_tooltip'
        );
        processedCol.label = `${col.label} ${suffix}`;
      }

      return processedCol;
    });
  });

  actions = signal<TableAction<TableRow>[]>([]);
  errors = signal<TableRowError<TableRow>[]>([]);
  currentIndexMap = signal<number[]>([]);
  isUploading = signal<boolean>(false);
  readonly firstTransformerModel = computed<DataModel>(() => {
    const transformer = this.selectedTransformer();
    const emptyModel = {
      required: false,
      metadata: {
        entityTypeVersion: '',
        schema: {}
      }
    };
    if (isNil(transformer)) {
      return emptyModel;
    }
    const models = transformer.transformerInfo.dataModels;
    return models.at(0) ?? emptyModel;
  });

  protected readonly rowSchema = computed(
    () => this.firstTransformerModel().metadata.schema as JSONSchemaType<TableRow>
  );

  private readonly referenceColumns = computed(() =>
    referenceColumnsOf(
      this.columns().map((column) => String(column.key)),
      this.firstTransformerModel().metadata.schema
    )
  );

  /**
   * Identifies columns from the CSV file that are not defined in the schema and will be
   * ignored during transformation.
   *
   * Returns a Set of column keys that should be marked with warnings.
   */
  protected ignoredColumnKeys = computed<Set<string>>(() => {
    const fileCols = this.fileColumns();
    const schema = this.rowSchema();
    const customLabelColumns = this.customLabelColumns();

    // Handle empty file columns
    if (fileCols.length === 0) {
      return new Set();
    }

    // Get properties from schema (check both root and items for arrays)
    const properties = schema.properties || schema['items']?.properties;

    // If no properties defined, check patternProperties and additionalProperties
    if (!properties || Object.keys(properties).length === 0) {
      const ignored = new Set<string>();

      for (const col of fileCols) {
        const colKey = String(col.key);

        // Custom-label column (cl_<labelKey>) accepted by the backend transformer
        if (customLabelColumns.has(colKey)) {
          continue;
        }

        // Check if matches any pattern
        if (matchesPatternProperties(colKey, schema.patternProperties)) {
          continue; // Valid via pattern
        }

        // Doesn't match any pattern - check additionalProperties
        const additionalProps = schema.additionalProperties ?? true;
        if (additionalProps === false) {
          ignored.add(colKey);
        }
      }

      return ignored;
    }

    // Build set of valid schema keys (O(1) lookup)
    const schemaKeys = new Set(Object.keys(properties));
    const ignored = new Set<string>();

    // Check each file column against schema
    for (const col of fileCols) {
      const colKey = String(col.key);

      // Custom-label column (cl_<labelKey>) accepted by the backend transformer.
      // These live in custom_data.labels, not in the entity schema properties.
      if (customLabelColumns.has(colKey)) {
        continue;
      }

      // Column is valid if it matches ANY of these (per JSON Schema spec):
      // 1. Explicitly defined in properties
      if (schemaKeys.has(colKey)) {
        continue; // Valid column, skip
      }

      // 2. Matches a pattern in patternProperties
      if (matchesPatternProperties(colKey, schema.patternProperties)) {
        continue; // Valid column, skip
      }

      // 3. Not matched by properties or patternProperties
      // Check if additionalProperties allows it
      const additionalProps = schema.additionalProperties ?? false;
      if (additionalProps === false) {
        // Explicitly forbidden - mark as ignored
        ignored.add(colKey);
      }
      // else: additionalProperties is true or a schema - column is accepted
    }

    return ignored;
  });

  protected csvFile: File | null = null;
  protected currentLang = toSignal(
    this.translate.onLangChange.pipe(
      filter((value) => !isNil(value)),
      map((value) => value.lang)
    ),
    { initialValue: this.translate.getCurrentLang() }
  );
  private readonly csvParserService = inject(CsvParserService);
  private acceptedFileTypes = ['text/csv'];
  private dataspace = this.store.selectSignal(selectDataspaceState);

  uploadConfig = computed<UploadZoneConfig>(() => {
    const transformerNames = this.availableTransformers().map(
      (transformer: TransformerInterface) => {
        const rawTransformerName = transformer.transformerDisplayLabel();
        return this.entityLabelsTranslationService.translateCsvTransformerName(
          rawTransformerName
        );
      }
    );

    this.currentLang();
    return {
      acceptedFileTypes: this.acceptedFileTypes,
      multiple: false,
      displayText: this.translate.instant('upload_zone.file_drop.message'),
      acceptedFileTypesText: this.translate.instant(
        'upload_zone.file_drop.accepted_file_types'
      ),
      onlyCaptureFiles: false,
      availableTransformers: transformerNames
    };
  });

  uploadConfigBelow = computed<UploadZoneConfig>(() => {
    this.currentLang();
    return {
      acceptedFileTypes: this.acceptedFileTypes,
      multiple: false,
      displayText: this.translate.instant('upload_zone.file_drop.message'),
      acceptedFileTypesText: this.translate.instant(
        'upload_zone.file_drop.accepted_file_types'
      ),
      onlyCaptureFiles: true,
      availableTransformers: []
    };
  });

  private readonly transformerBaseColumns = computed(() => {
    const transformer = this.selectedTransformer();
    if (isNil(transformer)) {
      console.warn('No transformer selected or available.');
      return [];
    }

    return transformer.colsInOrder().map((colName: string) => {
      return {
        key: colName as keyof TableRow,
        label: colName
      } as TableColumn<TableRow>;
    });
  });

  /** Number of parsed rows that currently pass schema validation (no attached errors). */
  protected readonly validRowCount = computed<number>(() => {
    const rowsWithErrors = new Set(this.errors().map((error) => error.row));
    const invalidRows = this.rows().filter((row) => rowsWithErrors.has(row)).length;
    return this.rows().length - invalidRows;
  });

  /** Translated display name of the target entity, for the header subtitle. */
  protected readonly entityDisplayName = computed<string>(() => {
    this.currentLang();
    return this.entityLabelsTranslationService.translateEntityModelName(this.entityType());
  });

  /** Clears the pending rows, errors and file so the user can start the import over. */
  protected resetUpload(): void {
    this.rows.set([]);
    this.errors.set([]);
    this.currentIndexMap.set([]);
    this.fileColumns.set([]);
    this.csvFile = null;
    this.parseSubscription?.unsubscribe();
    this.fileTransferService.clear();
    this.showPicker.set(true);
  }

  constructor() {
    super();

    effect(() => {
      if (this.currentIndexMap().length > 0) {
        this.setRowNumberWithIndexMap();
      }
    });
  }

  ngOnInit() {
    this.csvFile = this.fileTransferService.getFiles()?.[0] || null;

    if (this.csvFile) {
      this.handleFileUploaded();
      this.showPicker.set(false);
    } else {
      this.showPicker.set(true);
    }
  }

  override ngOnDestroy() {
    super.ngOnDestroy();
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  private convertRowValuesToExpectedTypes(row: TableRow, schema: any): TableRow {
    const convertedRow = { ...row };
    const properties = schema.properties || schema.items?.properties;
    const customLabelColumns = this.customLabelColumns();

    if (properties) {
      Object.keys(convertedRow).forEach((column) => {
        const allTypes = getPropertyTypes(schema, column);
        const acceptableTypes = allTypes.filter(isBoolStringNumber);
        const value = convertedRow[column];

        if (typeof value == 'string' && value.trim().length <= 0) {
          if (allTypes.includes('null')) {
            convertedRow[column] = null;
          } else {
            convertedRow[column] = undefined;
          }
          return;
        }

        // Custom-label columns (cl_<labelKey>) are not part of the entity schema, so
        // getPropertyTypes returns no types and the generic fallback below would always
        // parse them as numbers. Coerce them using the label's declared value type instead
        // (e.g. a string label whose CSV value is "13" must stay the string "13").
        const customLabel = customLabelColumns.get(column);
        if (customLabel) {
          convertedRow[column] = this.coerceCustomLabelValue(value, customLabel.valueType);
          return;
        }

        if (acceptableTypes.length > 0) {
          if (value !== null && value !== undefined) {
            if (acceptableTypes.includes('string')) {
              // we expect a string
              if (!acceptableTypes.includes('number') && typeof value === 'number') {
                // but the value was mistakenly interpreted as a number
                convertedRow[column] = value.toString();
              } else if (
                !acceptableTypes.includes('boolean') &&
                typeof value === 'boolean'
              ) {
                // but the value was mistakenly interpreted as a boolean
                convertedRow[column] = value.toString();
              }
            } else if (acceptableTypes.includes('number')) {
              // Handle comma decimal separator (e.g., "123,45" → 123.45)
              if (typeof value === 'string' && value.trim().match(numberRegex)) {
                // Handle comma decimal separator (e.g., "123,45" → 123.45)
                const normalizedValue = value.replace(',', '.');
                const numValue = Number(normalizedValue);
                if (!isNaN(numValue)) {
                  convertedRow[column] = numValue;
                }
              }
            } else if (acceptableTypes.includes('boolean')) {
              if (typeof value === 'string' && isBooleanString(value)) {
                convertedRow[column] = isTruthyString(value);
              }
            }
          }
        } else {
          // If it is an additional property, we try to parse to number first
          if (typeof value === 'string' && value.trim().match(numberRegex)) {
            // Handle comma decimal separator (e.g., "123,45" → 123.45)
            const normalizedValue = value.replace(',', '.');
            const numValue = Number(normalizedValue);
            if (!isNaN(numValue)) {
              convertedRow[column] = numValue;
            }
          }
        }
      });
    }

    return convertedRow;
  }

  /**
   * Coerces a raw CSV value for a custom-label column to the label's declared value type.
   *
   * CSV values always arrive as strings (the parser does not auto-type), so a string label
   * holding "13" must remain "13" while a numeric label holding "13" becomes the number 13.
   */
  private coerceCustomLabelValue(
    value: string | number | boolean | null | undefined,
    valueType: LabelValueType
  ): string | number | boolean | null | undefined {
    if (value === null || value === undefined) {
      return value;
    }

    switch (valueType) {
      case LabelValueType.NUMBER:
        if (typeof value === 'string' && value.trim().match(numberRegex)) {
          // Handle comma decimal separator (e.g., "123,45" → 123.45)
          const numValue = Number(value.replace(',', '.'));
          if (!isNaN(numValue)) {
            return numValue;
          }
        }
        return value;
      case LabelValueType.BOOL:
        if (typeof value === 'string' && isBooleanString(value)) {
          return isTruthyString(value);
        }
        return value;
      case LabelValueType.STRING:
      case LabelValueType.ENUM:
      case LabelValueType.REFERENCE:
      default:
        // Keep string-typed labels as strings even when the value looks numeric/boolean.
        if (typeof value === 'number' || typeof value === 'boolean') {
          return value.toString();
        }
        return value;
    }
  }

  handleFileUploaded() {
    if (!this.csvFile) {
      console.warn('No CSV file selected for upload.');
      return;
    }

    this.parseSubscription?.unsubscribe();
    this.isParsingCsv.set(true);
    this.parseSubscription = this.csvParserService
      .parseFile<TableRow>(this.csvFile)
      .pipe(finalize(() => this.isParsingCsv.set(false)))
      .subscribe({
        next: (result) => {
          if (result.errors.length > 0) {
            console.warn('CSV parsing errors:', result.errors);
          }

          const fileColumns: TableColumn<TableRow>[] = result.columns;
          this.fileColumns.set(fileColumns);

          const schema = this.rowSchema();
          const processedData = result.data.map((row) => {
            return this.convertRowValuesToExpectedTypes(row, schema);
          });

          const importErrors = processedData.flatMap((row) =>
            this.toTableRowErrors(this.checkIfRowIsValid(row).errors || [], row)
          );
          // One update for the whole file: updating per row re-sorts the full error list each
          // time, which freezes the page on large imports.
          this.replaceRowErrors(importErrors, this.rows().length + 1, processedData.length);

          this.rows.update((old) => [...old, ...processedData]);
        },
        error: (error) => {
          console.error('Failed to parse CSV file:', error);
        }
      });

    this.subscriptionsManager.add(this.parseSubscription);
  }

  onFilesDropUpload(event: UploadClicked): void {
    if (event.files.length > 0) {
      const translatedTransformerName = event.transformer;
      const transformer = this.availableTransformers().find((t) => {
        const rawName = t.transformerDisplayLabel();
        return (
          translatedTransformerName ===
          this.entityLabelsTranslationService.translateCsvTransformerName(rawName)
        );
      });
      const transformerName = transformer?.transformerDisplayLabel();

      this.csvFile = event.files[0];
      this.handleFileUploaded();
      this.router.navigate([], {
        relativeTo: this.activatedRoute,
        queryParams: { transformer: transformerName },
        queryParamsHandling: 'merge'
      });
      this.rows.set([]);
      this.showPicker.set(false);
    }
  }

  onFilesDropped(event: FilesDropped): void {
    const file = event.files[0];
    if (file) {
      this.importCsvFile(file);
    }
  }

  /** Handler for the hidden file input behind the "Browse" button. */
  protected onBrowseCsvSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    const file = input.files?.[0];
    if (file) {
      this.importCsvFile(file);
    }
    // Reset so selecting the same file again still fires a `change` event.
    input.value = '';
  }

  protected onCsvDragOver(event: DragEvent): void {
    event.preventDefault();
    this.isDraggingCsv.set(true);
  }

  protected onCsvDragLeave(event: DragEvent): void {
    // Ignore leave events that only move between descendants of the drop container.
    const related = event.relatedTarget as Node | null;
    const container = event.currentTarget as HTMLElement;
    if (!related || !container.contains(related)) {
      this.isDraggingCsv.set(false);
    }
  }

  protected onCsvDrop(event: DragEvent): void {
    event.preventDefault();
    this.isDraggingCsv.set(false);
    const file = event.dataTransfer?.files?.[0];
    if (file && this.isCsvFile(file)) {
      this.importCsvFile(file);
    }
  }

  /** Sets the pending file and runs the parse/validate/append pipeline shared by browse & drop. */
  private importCsvFile(file: File): void {
    this.csvFile = file;
    this.handleFileUploaded();
    this.showPicker.set(false);
  }

  private isCsvFile(file: File): boolean {
    return file.type === 'text/csv' || file.name.toLowerCase().endsWith('.csv');
  }

  convertColumnsAndRowsToJsonArray(): TableRow[] {
    const columns = this.columns();
    const rows = this.rows();

    if (columns.length === 0 || rows.length === 0) {
      return [];
    }

    return rows.map(stripNullFields);
  }

  handleCsvUploadWithTransformer() {
    const transformer = this.selectedTransformer();
    if (isNil(transformer) || isNil(this.rows())) {
      return;
    }
    const rows = this.convertColumnsAndRowsToJsonArray();
    const entityTypeVersion =
      transformer.transformerInfo.dataModels[0].metadata.entityTypeVersion;

    this.isUploading.set(true);
    // The failure is reported in the errors list and summarised by our own notification,
    // so the interceptor's generic one would repeat it at full, unreadable length.
    this.submitRows$(transformer.transformerInfo.transformer, rows, entityTypeVersion)
      .pipe(
        tap((result) => {
          console.info('API returned: ', result);
          this.isUploading.set(false);
          this.router.navigate(['..'], { relativeTo: this.activatedRoute });
        }),
        catchError((err) => {
          console.error('transformer errors: ', err);
          this.handleTransformerErrors(err);
          this.isUploading.set(false);
          return of(null);
        }),
        take(1)
      )
      .subscribe();
  }

  /**
   * Inside a plan the rows go to the plan's own batch route and global data is left
   * untouched — the point of the whole feature. Outside one, the global transformer route
   * is unchanged.
   */
  private submitRows$(
    transformer: Transformer,
    rows: TableRow[],
    entityTypeVersion: string
  ): Observable<DataTransformerSuccessResponse> {
    const planId = this.routePlanId();
    if (planId) {
      return this.plansService.createEntitiesBatch$(
        planId,
        this.entityType().toUpperCase(),
        {
          data: rows as Record<string, unknown>[],
          transformer: { name: transformer.name, version: transformer.version },
          entityTypeVersion
        },
        { skipErrorNotification: true }
      );
    }

    const transformerData: DataTransformerRequest = {
      transformer,
      dataModels: [{ data: rows, entityTypeVersion }]
    };
    return this.csvTransformersService.postTransformers(
      transformerData,
      this.dataspace().dataspace?.id ?? undefined,
      { skipErrorNotification: true }
    );
  }

  checkIfRowIsValid(row: TableRow): { isValid: boolean; errors?: ErrorObject[] | null } {
    const validate = this.ajv.compile(this.rowSchema());
    const valid = validate(row);

    if (!valid) {
      localize[this.translate.getCurrentLang() as AvailableLanguages](
        validate.errors || []
      );
    }

    return { isValid: valid, errors: validate.errors };
  }

  private setRowNumberWithIndexMap() {
    // indexes represent the ID of the orignal rows and the position in the current displayed table.
    // exemple : [1, 0, 2] means that the first row is the second original row, the second row is the first original row and the third row is the third original row.
    this.errors.update((old) => {
      const updatedErrors = old.map((error) => {
        const originalRowNumber = this.rows().indexOf(error.row);
        // An error naming no row of the table (an over-cap refusal, a finding whose row the
        // backend did not identify) has no number to renumber.
        if (originalRowNumber >= 0 && this.currentIndexMap().length > originalRowNumber) {
          error.rowNumber = this.currentIndexMap().indexOf(originalRowNumber) + 1;
        }
        return error;
      });
      return updatedErrors.sort((a, b) => rowOrder(a) - rowOrder(b));
    });
  }

  private toTableRowErrors(
    errors: ErrorObject[],
    row: TableRow
  ): TableRowError<TableRow>[] {
    return errors.map((error) => ({
      row: row,
      column: error.instancePath.slice(1) === '' ? undefined : error.instancePath.slice(1),
      message: error.message || error.keyword,
      backendError: false,
      completeDetails: error.instancePath.slice(1).length > 0
    }));
  }

  /**
   * Replaces the errors of rows `firstRowNumber` to `firstRowNumber + count - 1` with
   * `rowErrors`. Errors naming no row are kept.
   */
  private replaceRowErrors(
    rowErrors: TableRowError<TableRow>[],
    firstRowNumber: number,
    count: number
  ): void {
    const lastRowNumber = firstRowNumber + count - 1;
    this.errors.update((old) => {
      const kept = old.filter(
        (e) =>
          e.rowNumber === undefined ||
          e.rowNumber < firstRowNumber ||
          e.rowNumber > lastRowNumber
      );
      return [...kept, ...rowErrors].sort((a, b) => rowOrder(a) - rowOrder(b));
    });
  }

  handleValidationErrors(errors: ErrorObject[], row: TableRow, rowIndex: number) {
    this.replaceRowErrors(this.toTableRowErrors(errors, row), rowIndex + 1, 1);
  }

  handleRowEdition(rowEvent: RowEvent<TableRow>) {
    switch (rowEvent.type) {
      case 'removeCell':
        this.rows.update((old) => {
          const rowIndex = rowEvent.index;

          delete old[rowIndex][rowEvent.column];

          const validation = this.checkIfRowIsValid(old[rowIndex]);
          this.handleValidationErrors(validation.errors || [], old[rowIndex], rowIndex);

          return [...old];
        });
        break;
      case 'edit':
        this.rows.update((old) => {
          const rowIndex = rowEvent.index;

          old[rowIndex] = {
            ...old[rowIndex],
            [rowEvent.column]: rowEvent.newValue
          };
          old[rowIndex] = this.convertRowValuesToExpectedTypes(
            old[rowIndex],
            this.rowSchema()
          );

          const validation = this.checkIfRowIsValid(old[rowIndex]);
          this.handleValidationErrors(validation.errors || [], old[rowIndex], rowIndex);

          return [...old];
        });
        break;
      case 'add':
        this.rows.update((old) => {
          const newRow = this.convertRowValuesToExpectedTypes(
            rowEvent.row,
            this.rowSchema()
          );

          const rowIndex = old.length;
          old.push(newRow);

          const validation = this.checkIfRowIsValid(newRow);
          this.handleValidationErrors(validation.errors || [], newRow, rowIndex);

          return [...old];
        });

        break;
      case 'delete':
        this.rows.update((old) => {
          const rowIndex = rowEvent.index;
          if (rowIndex > -1) {
            old.splice(rowIndex, 1);
          }

          this.errors.update((errs) => {
            const filtered = errs.filter((e) => e.rowNumber !== rowIndex + 1);
            filtered.map((e) => {
              if (e.rowNumber && e.rowNumber > rowIndex + 1) {
                e.rowNumber = e.rowNumber - 1;
              }
              return e;
            });
            return filtered;
          });
          return [...old];
        });
        break;
      default:
        console.error('Row event type not implemented');
        break;
    }
  }

  /**
   * Attaches a failed import's findings to the rows — and, where the finding names a uid the
   * table holds, the individual cells — they are about.
   *
   * The API indexes the array it was sent from 0, while the table numbers those same
   * records from 1: this is the single place the two numberings meet, so `rowNumber` is the
   * only value the +1 is applied to. A finding naming no record — or one whose index is
   * outside the rows still on screen — stays a global error rather than being dropped.
   */
  private handleTransformerErrors(err: GbeError) {
    const occurrences = this.gbeErrorParserService
      .resolveError(err)
      .flatMap((view) => view.occurrences);
    const rows = this.rows();

    const errors: TableRowError<TableRow>[] = [];
    for (const occurrence of occurrences) {
      const message = occurrenceText(occurrence);
      const rowIndices = (occurrence.rowIndices ?? []).filter(
        (index) => index < rows.length
      );

      // A finding naming records is only about those; one naming none — `missing_reference`
      // lists the uids it could not resolve and nowhere they came from — may still be traced
      // to a cell, so every parsed record is a candidate for it.
      const candidates: NumberedRow<TableRow>[] = (
        rowIndices.length > 0 ? rowIndices : rows.map((_, index) => index)
      ).map((index) => ({ row: rows[index], rowNumber: index + 1 }));

      const cells = findReferenceCells(
        occurrence.refs,
        candidates,
        this.referenceColumns()
      );
      if (cells.length > 0) {
        errors.push(...this.cellErrors(cells, message));
        continue;
      }

      if (rowIndices.length === 0) {
        // TODO(DataLayer structured row index): a formula-materialization or site-geocoding
        // failure states its record inside its own prose ("column formula (row 4657)") and
        // nowhere else, so it lands here still counting from 0. Route it through the branch
        // above once those payloads carry the index as a field.
        errors.push({ row: {}, completeDetails: false, backendError: true, message });
        continue;
      }

      // Every named row, so the errors list can state a finding spanning several of them
      // once ("Lines 1, 3") instead of repeating it under each.
      const rowNumbers = rowIndices.map((index) => index + 1);
      for (const rowNumber of rowNumbers) {
        errors.push({
          row: rows[rowNumber - 1],
          rowNumber,
          rowNumbers,
          completeDetails: false,
          backendError: true,
          message
        });
      }
    }

    this.errors.set([...errors].sort((a, b) => rowOrder(a) - rowOrder(b)));
    this.notifyImportFailed(errors);
  }

  /**
   * One error per named cell, so the table paints it red and the errors list names the
   * column. `rowNumbers` is shared per column rather than across the whole finding: the same
   * broken reference spread over several rows is one entry in the list, but two different
   * columns of it stay two, each naming only the rows it was found in.
   */
  private cellErrors(
    cells: ReferenceCell<TableRow>[],
    message: string
  ): TableRowError<TableRow>[] {
    const rowNumbersByColumn = new Map<string, number[]>();
    for (const cell of cells) {
      const rowNumbers = rowNumbersByColumn.get(cell.column) ?? [];
      rowNumbers.push(cell.rowNumber);
      rowNumbersByColumn.set(cell.column, rowNumbers);
    }

    return cells.map((cell) => ({
      row: cell.row,
      rowNumber: cell.rowNumber,
      rowNumbers: rowNumbersByColumn.get(cell.column),
      column: cell.column,
      completeDetails: false,
      backendError: true,
      message
    }));
  }

  /**
   * Summarises the failure, since the errors list can be scrolled out of view and holds one
   * entry per affected row. Falls back to the findings themselves when none names a row,
   * which is the only case where there is nothing to count.
   */
  private notifyImportFailed(errors: TableRowError<TableRow>[]): void {
    const affectedRows = new Set(
      errors.filter((error) => !isNil(error.rowNumber)).map((error) => error.rowNumber)
    ).size;

    const message =
      affectedRows > 0
        ? this.translate.instant('features.data.upload.import_failed_rows', {
            count: affectedRows
          })
        : errors.map((error) => error.message).join(', ');

    this.notificationService.error(
      this.translate.instant('features.data.upload.import_failed_title'),
      message
    );
  }

  protected createTransformerSelectItems(): SelectItem[] {
    return this.availableTransformers().map((transformer) => ({
      id: transformer.transformerDisplayLabel(),
      label: this.entityLabelsTranslationService.translateCsvTransformerName(
        transformer.transformerDisplayLabel()
      )
    }));
  }

  /** Id of the currently selected transformer, for the select-dropdown `value` binding. */
  protected selectedTransformerId = computed<SelectId | null>(
    () => this.selectedTransformer()?.transformerDisplayLabel() ?? null
  );

  /** Human-readable (translated) name of the selected transformer, for the drop-strip hint. */
  protected selectedTransformerName = computed<string>(() => {
    const transformer = this.selectedTransformer();
    return transformer
      ? this.entityLabelsTranslationService.translateCsvTransformerName(
          transformer.transformerDisplayLabel()
        )
      : '';
  });

  /** Selecting a transformer navigates via the `transformer` query param and resets the pending rows. */
  protected onTransformerSelect(value: ButtonSelectValue): void {
    const id = Array.isArray(value) ? value[0] : value;
    if (isNil(id)) {
      return;
    }
    this.router.navigate([], {
      relativeTo: this.activatedRoute,
      queryParams: { transformer: id },
      queryParamsHandling: 'merge'
    });
    this.rows.set([]);
  }

  reIndexErrors(indexes: number[]) {
    this.currentIndexMap.set(indexes);
  }

  protected readonly ICONS = ICONS;
}
